import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  ListItem,
  ListItemText,
  List,
  ListItemIcon,
  Skeleton,
} from '@mui/material';
import { useLocalization } from '@/hooks/useLocalization';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import { StandardWidgetProps } from '@/models/WidgetModels';
import { ModuleActionButtons, quickLinkModuleDefinitions } from '@/permissions';
import { ExternalLink } from '@/components/ExternalLink';
import { mobileWidth } from '@/constants';
import { brandedLinkColor, minimalScrollbarStyling } from '@/constants/styles';
import { ContentContainer } from '../contentContainer';
import { useQuickLinks } from './dashboard-quick-links/useQuickLinks';
import { useSocialLinks } from './dashboard-quick-links/useSocialLinks';
import { useEConsent } from './dashboard-quick-links/useEConsent';

type DashboardQuickLinksProps = StandardWidgetProps;

export default function DashboardQuickLinks({
  onDataLoaded,
  isWidget,
  isHalfWidth = true,
  title,
  container = 'widget',
}: DashboardQuickLinksProps) {
  const { t } = useLocalization();
  const widgetTitle = title ?? t('quickLinks');
  const isMobile = useMediaQuery(mobileWidth);

  const {
    sortedQuickLinks,
    quickLinksData,
    hasLinks,
    isQuickLinksDataLoading,
    isQuickLinksSettingLoading,
    quickLinksDataError,
    quickLinkSettingsError,
  } = useQuickLinks(isMobile);

  const { activeSocialLinks } = useSocialLinks();

  const { getModuleLabel, isQuickLinkModuleVisible } = useEConsent();

  const isLoading = isQuickLinksDataLoading || isQuickLinksSettingLoading;
  const isError = quickLinksDataError || quickLinkSettingsError;

  const hasModuleActions = useMemo(
    () =>
      Object.values(quickLinkModuleDefinitions).some((module) =>
        isQuickLinkModuleVisible(module),
      ),
    [isQuickLinkModuleVisible],
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showBottomFade, setShowBottomFade] = useState(false);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  const checkScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const hasOverflow = container.scrollHeight > container.clientHeight;
    const isAtBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < 2;

    setShowBottomFade(hasOverflow && !isAtBottom);
    setScrollbarWidth(container.offsetWidth - container.clientWidth);
  }, []);

  useEffect(() => {
    checkScrollPosition();
  }, [sortedQuickLinks, checkScrollPosition]);

  useEffect(() => {
    if (quickLinksData) {
      onDataLoaded?.();
    }
  }, [quickLinksData, onDataLoaded]);

  const socialIconsJsx =
    activeSocialLinks.length > 0 ? (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {activeSocialLinks.map(({ key, Icon, url, label }) => (
          <IconButton
            key={key}
            component='a'
            href={url}
            target='_blank'
            rel='noopener noreferrer'
            aria-label={label}
            sx={{
              color: 'primary.main',
              borderRadius: 1,
              p: 0.5,
              border: 'none',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: 'action.selected',
              },
            }}
          >
            <Icon fontSize='small' />
          </IconButton>
        ))}
      </Box>
    ) : null;

  const linksListJsx =
    sortedQuickLinks.length > 0 ? (
      <Box sx={{ position: 'relative', flex: 1 }}>
        <Box
          ref={scrollContainerRef}
          onScroll={checkScrollPosition}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            maxHeight: 400,
            overflowY: 'auto',
            ...minimalScrollbarStyling,
          }}
        >
          <List>
            {sortedQuickLinks.map((link) => (
              <ListItem
                key={link.Name}
                disablePadding
              >
                <ExternalLink
                  url={link.Url}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    width: '100%',
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    color: 'inherit',
                    textDecoration: 'none',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: '32px',
                      margin: '6px 0',
                    }}
                  >
                    <LaunchOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={link.Name}
                    secondary={link.Description}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: '600',
                        fontSize: '14px',
                        color: brandedLinkColor,
                      },
                      '& .MuiListItemText-secondary': {
                        fontSize: '13px',
                      },
                    }}
                  />
                </ExternalLink>
              </ListItem>
            ))}
          </List>
        </Box>
        {showBottomFade && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: scrollbarWidth,
              height: 40,
              background: (theme) =>
                `linear-gradient(transparent, ${theme.palette.background.paper})`,
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>
    ) : null;

  if (isLoading || isError) {
    return (
      <ContentContainer container={container}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            height: isWidget ? 'fit-content' : '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant='h5'>{widgetTitle}</Typography>
          </Box>

          {isLoading && (
            <>
              {' '}
              {Array.from({ length: 5 }).map((_, idx) => (
                <Skeleton key={idx} />
              ))}
            </>
          )}

          {isError && (
            <Typography>
              {t(
                'quickLinks.error',
                'Unable to load Quick Links. Please try again later.',
              )}
            </Typography>
          )}
        </Box>
      </ContentContainer>
    );
  }

  if (isHalfWidth) {
    return (
      <>
        <ContentContainer container={container}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              height: isWidget ? 'fit-content' : '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant='h5'>{widgetTitle}</Typography>
              {socialIconsJsx}
            </Box>

            <ModuleActionButtons
              definitions={quickLinkModuleDefinitions}
              variant='outlined'
              getModuleLabel={getModuleLabel}
              isModuleVisible={isQuickLinkModuleVisible}
            />

            {linksListJsx}
          </Box>
        </ContentContainer>
      </>
    );
  }

  return (
    <>
      <ContentContainer container={container}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            height: isWidget ? 'fit-content' : '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant='h5'>{widgetTitle}</Typography>
            {socialIconsJsx}
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 3,
              flexWrap: { xs: 'wrap', md: 'nowrap' },
            }}
          >
            {hasModuleActions && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  width: {
                    xs: '100%',
                    md: hasLinks ? '30%' : '100%',
                  },
                  '& .MuiButton-root': {
                    width: '100%',
                  },
                }}
              >
                <ModuleActionButtons
                  definitions={quickLinkModuleDefinitions}
                  variant='outlined'
                  getModuleLabel={getModuleLabel}
                  isModuleVisible={isQuickLinkModuleVisible}
                />
              </Box>
            )}

            {hasLinks && (
              <Box
                sx={{
                  width: {
                    xs: '100%',
                    md: hasModuleActions ? '70%' : '100%',
                  },
                  minWidth: 0,
                }}
              >
                {linksListJsx}
              </Box>
            )}
          </Box>
        </Box>
      </ContentContainer>
    </>
  );
}
